import React, { useEffect, useMemo, useState } from 'react';
import { Link, generatePath, useNavigate } from 'react-router-dom';
import { Activity, AlertTriangle, DollarSign, TrendingDown, X } from 'lucide-react';

import { useGetTickets } from '@/api/tickets/useGetTickets';
import { useCreateTicketFromSignal } from '@/api/churn-signals/useCreateTicketFromSignal';
import { SIGNAL_STATUS, useUpdateSignalStatus } from '@/api/churn-signals/useUpdateSignalStatus';
import { Button } from '@/component-library/primitives/Button/Button';
import { useStripeFinancialFeaturesEnabled } from '@/hooks/useStripeFinancialFeaturesEnabled';
import { useTracking } from '@/hooks/useTracking';
import { ChurnSignal } from '@/models/DashboardData';
import { firstChurnSignalGuid } from '@/utils/churnSignalUuid';
import { normalizeSignalStatus } from '@/utils/signalStatus';
import { getSignalIdFromTicketMetadata } from '@/utils/ticketSignalMetadata';
import { AppRoutes } from '@/utils/routes/router';

import NotRelevantSignalModal from './NotRelevantSignalModal';
import { RecommendedActionExplanationModal } from './RecommendedActionExplanationModal';

interface ChurnSignalModalProps {
  isOpen: boolean;
  onClose: () => void;
  signal: ChurnSignal | null;
}

const ChurnSignalModal: React.FC<ChurnSignalModalProps> = ({ isOpen, onClose, signal }) => {
  const navigate = useNavigate();
  const { trackEvent } = useTracking();
  const createTicketMutation = useCreateTicketFromSignal();
  const updateStatusMutation = useUpdateSignalStatus();
  const showStripeFinancial = useStripeFinancialFeaturesEnabled();
  const [actionExplanationOpen, setActionExplanationOpen] = useState(false);
  const [selectedRecommendedAction, setSelectedRecommendedAction] = useState<string | null>(null);
  const [notRelevantModalOpen, setNotRelevantModalOpen] = useState(false);

  const signalUuid = signal ? firstChurnSignalGuid(signal.id, signal.signalId) : null;

  const { data: ticketsPayload } = useGetTickets({ includeArchived: true, limit: 500 });
  const linkedTicket = useMemo(() => {
    if (!signalUuid || !ticketsPayload?.tickets?.length) return null;
    return (
      ticketsPayload.tickets.find((t) => getSignalIdFromTicketMetadata(t.metadata) === signalUuid) ?? null
    );
  }, [signalUuid, ticketsPayload?.tickets]);

  useEffect(() => {
    if (!isOpen) {
      setActionExplanationOpen(false);
      setSelectedRecommendedAction(null);
      setNotRelevantModalOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !signal || !signalUuid) return;
    trackEvent('churn_signal_viewed', {
      signal_id: signalUuid,
      signal_type: signal.signalType,
      priority: signal.priority,
      risk_score: signal.riskScore,
      status: signal.status,
    });
  }, [isOpen, signalUuid, signal?.signalType, signal?.priority, signal?.riskScore, signal?.status, trackEvent]);

  if (!isOpen || !signal) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
      case 'high':
        return 'bg-custom-red/10 text-custom-red border-custom-red/20';
      case 'medium':
        return 'bg-custom-yellow/10 text-custom-yellow border-custom-yellow/20';
      case 'low':
        return 'bg-custom-green/10 text-custom-green border-custom-green/20';
      default:
        return 'bg-custom-blue/10 text-custom-blue border-custom-blue/20';
    }
  };

  const getSignalTypeIcon = (signalType: string) => {
    switch (signalType) {
      case 'high_risk_churn':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'medium_risk_churn':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'low_activity':
        return <TrendingDown className="w-5 h-5 text-orange-500" />;
      case 'payment_failure':
        return <DollarSign className="w-5 h-5 text-red-500" />;
      case 'subscription_downgrade':
        return <TrendingDown className="w-5 h-5 text-yellow-500" />;
      default:
        return <Activity className="w-5 h-5 text-custom-black" />;
    }
  };

  const isDismissed = normalizeSignalStatus(signal) === 'dismissed';

  const handleRestore = async () => {
    if (!signalUuid) return;
    try {
      await updateStatusMutation.mutateAsync({ signalId: signalUuid, status: SIGNAL_STATUS.New });
      trackEvent('signal_restored_from_not_relevant', { signal_id: signalUuid });
      onClose();
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreateTicket = async () => {
    if (!signalUuid) {
      console.error('Cannot create ticket: signal has no id');
      return;
    }
    try {
      await createTicketMutation.mutateAsync(signalUuid);
      setActionExplanationOpen(false);
      setSelectedRecommendedAction(null);
      onClose();
      navigate(AppRoutes.tickets);
    } catch (error) {
      console.error('Error creating ticket:', error);
    }
  };

  const handleBackdropClick = () => {
    if (actionExplanationOpen) return;
    if (notRelevantModalOpen) return;
    onClose();
  };

  const openActionExplanation = (action: string) => {
    setSelectedRecommendedAction(action);
    setActionExplanationOpen(true);
  };

  const closeActionExplanation = () => {
    setActionExplanationOpen(false);
    setSelectedRecommendedAction(null);
  };

  return (
    <>
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-custom-mediumSilver">
          <div className="flex items-center space-x-3">
            {getSignalTypeIcon(signal.signalType)}
            <div>
              <h2 className="text-xl font-semibold text-custom-black">{signal.title}</h2>
              <p className="text-sm text-custom-black">Signal ID: {signal.signalId}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-custom-darkSilver hover:text-custom-black transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Main Info Grid */}
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              {/* Customer Info */}
              <div>
                <h3 className="text-sm font-medium text-custom-black mb-2">Customer</h3>
                <div className="space-y-1">
                  {signal.userId ? (
                    <Link
                      to={generatePath(AppRoutes.memberProfile, { id: signal.userId })}
                      className="text-sm font-semibold text-custom-blue hover:text-custom-darkBlue hover:underline"
                    >
                      {signal.userName || signal.userEmail || 'Member'}
                    </Link>
                  ) : (
                    <span className="text-sm font-semibold text-custom-black">
                      {signal.userName || signal.userEmail || 'Member'}
                    </span>
                  )}
                  <p className="text-xs text-custom-black">{signal.userEmail}</p>
                  <p className="text-xs text-custom-darkSilver">ID: {signal.userId}</p>
                </div>
              </div>

              {/* Signal Info */}
              <div>
                <h3 className="text-sm font-medium text-custom-black mb-2">Signal Details</h3>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-custom-darkSilver">Type:</span>
                    <span className="text-xs text-custom-black capitalize">{signal.signalType.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-custom-darkSilver">Risk Score:</span>
                    <span className="text-xs font-semibold text-custom-black">{signal.riskScore}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-custom-darkSilver">Priority:</span>
                    <span
                      className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full border ${getPriorityColor(
                        signal.priority,
                      )}`}
                    >
                      {signal.priority.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-custom-darkSilver">Status:</span>
                    <span className="text-xs text-custom-black capitalize">
                      {isDismissed ? 'not relevant' : signal.status?.replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Churn Risk */}
              {signal.churnRiskData && (
                <div>
                  <h3 className="text-sm font-medium text-custom-black mb-2">Risk Analysis</h3>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-custom-darkSilver">Churn Probability:</span>
                      <span className="text-xs font-semibold text-custom-black">
                        {signal.churnRiskData.churnPrediction?.churnProbability
                          ? `${(signal.churnRiskData.churnPrediction.churnProbability * 100).toFixed(1)}%`
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-custom-darkSilver">Confidence:</span>
                      <span className="text-xs text-custom-black">
                        {signal.churnRiskData.churnPrediction?.confidenceScore
                          ? `${(signal.churnRiskData.churnPrediction.confidenceScore * 100).toFixed(1)}%`
                          : 'N/A'}
                      </span>
                    </div>
                    {showStripeFinancial && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-custom-darkSilver">LTV:</span>
                        <span className="text-xs text-custom-black">
                          {signal.churnRiskData.lifetimeValue
                            ? `$${signal.churnRiskData.lifetimeValue.toLocaleString()}`
                            : 'N/A'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              {/* Description */}
              <div>
                <h3 className="text-sm font-medium text-custom-black mb-2">Description</h3>
                <p className="text-xs text-custom-black bg-custom-silver rounded p-2 line-clamp-3 overflow-hidden">
                  {signal.description}
                </p>
              </div>

              {/* Risk Factors */}
              {signal.churnRiskData?.riskFactors &&
                Array.isArray(signal.churnRiskData.riskFactors) &&
                signal.churnRiskData.riskFactors.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-custom-black mb-2">Risk Factors</h3>
                    <div className="space-y-1">
                      {signal.churnRiskData.riskFactors.slice(0, 3).map((factor: any, index: number) => {
                        // Handle both object format (with severity/description) and string format
                        const isObject = typeof factor === 'object' && factor !== null;
                        const description = isObject ? factor.description : factor;
                        const severity = isObject ? factor.severity : undefined;

                        return (
                          <div key={index} className="flex items-center space-x-2">
                            {severity && (
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${
                                  severity === 'high'
                                    ? 'bg-red-500'
                                    : severity === 'medium'
                                    ? 'bg-yellow-500'
                                    : 'bg-green-500'
                                }`}
                              />
                            )}
                            <span className="text-xs text-custom-darkSilver">{description}</span>
                          </div>
                        );
                      })}
                      {signal.churnRiskData.riskFactors.length > 3 && (
                        <p className="text-xs text-custom-darkSilver">
                          +{signal.churnRiskData.riskFactors.length - 3} more
                        </p>
                      )}
                    </div>
                  </div>
                )}

              {/* Recommended Actions */}
              {signal.recommendedActions && signal.recommendedActions.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-custom-black mb-2">Recommended Actions</h3>
                  <p className="text-[11px] text-custom-darkSilver mb-2">
                    Select an action to see why we suggested it and what it is meant to address.
                  </p>
                  <div className="space-y-1">
                    {signal.recommendedActions.map((action: string, index: number) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => openActionExplanation(action)}
                        className="group flex w-full items-start space-x-2 rounded-md px-1 py-1.5 text-left transition-colors hover:bg-custom-silver/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-custom-blue/40"
                      >
                        <span className="text-custom-blue text-xs mt-0.5 shrink-0">•</span>
                        <span className="text-xs text-custom-darkSilver group-hover:text-custom-black">{action}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Timestamp */}
              <div>
                <h3 className="text-sm font-medium text-custom-black mb-2">Created</h3>
                <p className="text-xs text-custom-darkSilver">{formatDate(signal.createdAt)}</p>
              </div>
            </div>
          </div>
        </div>

        {isDismissed && signal.dismissedReason ? (
          <div className="px-6 pb-4">
            <h3 className="text-sm font-medium text-custom-black mb-1">Why not relevant</h3>
            <p className="text-xs text-custom-darkSilver whitespace-pre-wrap bg-custom-silver rounded p-3 border border-custom-lightSilver">
              {signal.dismissedReason}
            </p>
          </div>
        ) : null}

        {linkedTicket ? (
          <div className="px-6 py-3 border-t border-custom-lightSilver">
            <p className="text-xs font-medium text-custom-darkSilver mb-1">Ticket</p>
            <Link
              to={`${AppRoutes.tickets}?openTicket=${encodeURIComponent(linkedTicket.ticketId)}`}
              className="text-sm text-custom-blue font-semibold hover:underline"
              onClick={onClose}
            >
              {linkedTicket.title}
            </Link>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center justify-end gap-2 p-4 border-t border-custom-mediumSilver bg-custom-silver rounded-b-lg">
          <Button variant="transparent" onClick={onClose}>
            Close
          </Button>
          {isDismissed ? (
            <Button variant="secondary" onClick={handleRestore} disabled={updateStatusMutation.isLoading}>
              {updateStatusMutation.isLoading ? 'Restoring…' : 'Restore to active'}
            </Button>
          ) : (
            <Button
              type="button"
              variant="secondary"
              onClick={() => setNotRelevantModalOpen(true)}
              disabled={!signalUuid}
            >
              Not relevant
            </Button>
          )}
          <Button variant="primary" onClick={handleCreateTicket} disabled={createTicketMutation.isLoading}>
            {createTicketMutation.isLoading ? 'Creating...' : 'Create Ticket'}
          </Button>
        </div>
      </div>

      {actionExplanationOpen && selectedRecommendedAction && (
        <RecommendedActionExplanationModal
          isOpen
          onClose={closeActionExplanation}
          action={selectedRecommendedAction}
          context={{ type: 'signal', signal }}
          closeButtonLabel="Back"
          onSuccessfulTicketCreate={() => {
            setActionExplanationOpen(false);
            setSelectedRecommendedAction(null);
            onClose();
          }}
        />
      )}
    </div>

    {signalUuid ? (
      <NotRelevantSignalModal
        isOpen={notRelevantModalOpen}
        onClose={() => setNotRelevantModalOpen(false)}
        signalTitle={signal.title}
        signalUuid={signalUuid}
        onSuccess={() => {
          setNotRelevantModalOpen(false);
          onClose();
        }}
      />
    ) : null}
    </>
  );
};

export default ChurnSignalModal;
